import type { ReactNode } from "react";
import { Cargando } from "./Cargando";
import { EstadoVacio } from "./EstadoVacio";

export interface ColumnaTabla<T> {
  encabezado: string;
  render: (fila: T) => ReactNode;
  className?: string;
}

// Se usa `outline` (no `ring`, que es box-shadow) porque box-shadow en
// elementos <tr> tiene soporte inconsistente entre navegadores.
const FOCO = "focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-secundario";

export function Tabla<T>({
  columnas,
  filas,
  claveFila,
  onFilaClick,
  cargando = false,
  tituloVacio = "Sin resultados",
  descripcionVacio,
}: {
  columnas: ColumnaTabla<T>[];
  filas: T[];
  claveFila: (fila: T) => string;
  onFilaClick?: (fila: T) => void;
  cargando?: boolean;
  tituloVacio?: string;
  descripcionVacio?: string;
}) {
  if (cargando) return <Cargando />;
  if (filas.length === 0) return <EstadoVacio titulo={tituloVacio} descripcion={descripcionVacio} />;

  return (
    <div className="overflow-x-auto rounded-xl border border-borde bg-superficie shadow-sm">
      <table className="w-full min-w-full divide-y divide-borde text-sm">
        <thead className="bg-fondo">
          <tr>
            {columnas.map((col) => (
              <th
                key={col.encabezado}
                className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-texto-secundario"
              >
                {col.encabezado}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-borde">
          {filas.map((fila) => (
            <tr
              key={claveFila(fila)}
              onClick={onFilaClick ? () => onFilaClick(fila) : undefined}
              tabIndex={onFilaClick ? 0 : undefined}
              role={onFilaClick ? "button" : undefined}
              onKeyDown={
                onFilaClick
                  ? (evento) => {
                      if (evento.key === "Enter" || evento.key === " ") {
                        evento.preventDefault();
                        onFilaClick(fila);
                      }
                    }
                  : undefined
              }
              className={onFilaClick ? `cursor-pointer transition-colors hover:bg-fondo ${FOCO}` : ""}
            >
              {columnas.map((col) => (
                <td key={col.encabezado} className={`px-4 py-3 text-texto ${col.className ?? ""}`}>
                  {col.render(fila)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
